import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import Modals from "./Modal";
import {
  fetchInvoiceFollowupsByInvoice,
  getInvoiceFollowupErrorMessage,
  updateInvoiceFollowup,
} from "../../utilis/invoiceFollowupApi";
import { formatInvoiceFollowupDescription } from "../../utilis/invoiceFollowupHelpers";

function InvoiceFollowupsTabPanel({
  financeInvoiceId = null,
  users = [],
  refreshKey = 0,
  active = true,
  onFollowupsLoaded,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(() =>
    Boolean(active && financeInvoiceId),
  );
  const [error, setError] = useState("");
  const [resolveId, setResolveId] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const loadedKeyRef = useRef("");

  const load = useCallback(async () => {
    if (!financeInvoiceId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await fetchInvoiceFollowupsByInvoice(financeInvoiceId);
      setRows(Array.isArray(data) ? data : []);
      onFollowupsLoaded?.(data);
      return true;
    } catch (err) {
      setError(getInvoiceFollowupErrorMessage(err, err?.response?.data));
      setRows([]);
      return false;
    } finally {
      setLoading(false);
    }
  }, [financeInvoiceId, onFollowupsLoaded]);

  useEffect(() => {
    loadedKeyRef.current = "";
    setRows([]);
    setError("");
  }, [financeInvoiceId]);

  useEffect(() => {
    if (!active || !financeInvoiceId) return;
    const cacheKey = `${financeInvoiceId}:${refreshKey}`;
    if (loadedKeyRef.current === cacheKey) return;
    loadedKeyRef.current = cacheKey;
    load();
  }, [active, financeInvoiceId, refreshKey, load]);

  const handleCompleteCallback = async (
    _show,
    type,
    note,
    notetype,
    rescheduleDate,
    reassignId,
  ) => {
    if (type !== "edit") {
      setShowCompleteModal(false);
      setResolveId(null);
      return;
    }
    if (!resolveId) {
      setShowCompleteModal(false);
      return;
    }

    const resultText = String(note || "").trim();
    if (!resultText) {
      Swal.fire({
        icon: "warning",
        text: "Please enter a note before completing the follow-up.",
      });
      return;
    }

    const wantsReschedule =
      rescheduleDate !== false &&
      rescheduleDate !== "" &&
      rescheduleDate != null &&
      Boolean(String(rescheduleDate).trim());
    if (wantsReschedule && !String(rescheduleDate).trim()) {
      Swal.fire({
        icon: "warning",
        text: "Please select a reschedule date.",
      });
      return;
    }

    const reassignNumeric = parseInt(reassignId, 10);
    const wantsReassign =
      reassignId !== false &&
      reassignId !== "" &&
      reassignId != null &&
      Number.isFinite(reassignNumeric) &&
      reassignNumeric > 0;

    try {
      const data = await updateInvoiceFollowup(resolveId, {
        result: resultText,
        reschedule: wantsReschedule,
        rescheduleDate: wantsReschedule ? String(rescheduleDate).trim() : undefined,
        reassign: wantsReassign,
        reAssignID: wantsReassign ? reassignNumeric : undefined,
        noteType: notetype,
      });
      if (data?.error) {
        Swal.fire({ icon: "error", text: data.error });
        return;
      }
      setShowCompleteModal(false);
      setResolveId(null);
      Swal.fire({
        icon: "success",
        title: data?.message || "Follow-up updated",
        timer: 1500,
        showConfirmButton: false,
      });
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: getInvoiceFollowupErrorMessage(err, err?.response?.data),
      });
    }
  };

  if (!financeInvoiceId) {
    return (
      <p className="mb-0 py-6 text-center text-[12px] text-slate-500">
        Save this invoice before adding follow-ups.
      </p>
    );
  }

  if (loading) {
    return (
      <p className="mb-0 py-6 text-center text-[12px] text-slate-500">
        Loading follow-ups…
      </p>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-700">
        {error}
        <button
          type="button"
          className="ml-2 font-semibold underline"
          onClick={load}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <p className="mb-0 py-8 text-center text-[12px] text-slate-500">
        No follow-ups for this invoice yet.
      </p>
    );
  }

  return (
    <>
      <ul className="mb-0 list-none space-y-2 p-0">
        {rows.map((row) => (
          <li
            key={row.id}
            className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      row.status === "pending"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-emerald-100 text-emerald-800"
                    }`}
                  >
                    {row.status}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {moment(row.followup_date).format("D MMM YYYY")}
                  </span>
                  {row.invoice_number ? (
                    <span className="text-[11px] font-medium text-slate-600">
                      Inv. {row.invoice_number}
                    </span>
                  ) : null}
                </div>
                <p
                  className="mb-0 mt-1 text-[12px] text-slate-800"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {formatInvoiceFollowupDescription(row.description)}
                </p>
                {row.result ? (
                  <p className="mb-0 mt-1 text-[11px] text-slate-600">
                    <span className="font-semibold">Result:</span> {row.result}
                  </p>
                ) : null}
                <p className="mb-0 mt-1 text-[11px] text-slate-500">
                  Assigned: {row.assignee_name || "—"}
                </p>
              </div>
              {row.status === "pending" ? (
                <button
                  type="button"
                  className="btn btn-sm btn-success shrink-0"
                  onClick={() => {
                    setResolveId(row.id);
                    setShowCompleteModal(true);
                  }}
                >
                  Complete
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <Modals
        show={showCompleteModal}
        title="Add Completed Note"
        data="Add Note"
        type="category"
        reschedule={true}
        textarea="true"
        reassign={true}
        invoiceFollowupComplete={true}
        allUsers={users}
        assignedTo={resolveId}
        parentCallback={handleCompleteCallback}
      />
    </>
  );
}

export default InvoiceFollowupsTabPanel;
